import { useMemo, useState } from "react";
import { Link } from "react-router";
import { GitBranch, Headphones, MessageCircle, Share } from "lucide-react";
import type { FeedCard, FeedTake } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { relativeTime } from "@web-shared/lib/format";
import { profilePath, seedPath } from "@/lib/ctas";
import { numberedTakes } from "@/lib/feed-takes";
import { cn } from "@web-shared/lib/utils";
import { usePlayer, useTrackStatus, type PlayerStatus, type PlayerTrack } from "../player";
import { Avatar } from "./Avatar";
import { SeedCover } from "./SeedCover";
import { Waveform } from "./Waveform";
import { LikeButton } from "./LikeButton";
import { RemixCtas } from "./RemixCtas";
import { StartJamButton } from "./StartJamButton";
import { useFeedCta } from "./feed-cta";
import { RemixByline, OriginalButton } from "./RemixCredit";
import { shareSeed } from "./share";


export function feedTrack(card: FeedCard, take?: FeedTake): PlayerTrack {
  const t = take ?? { id: card.id, audioUrl: card.audioUrl };
  return {
    id: t.id,
    title: card.title,
    url: t.audioUrl,
    producer: { handle: card.producer.handle ?? card.producer.id, username: card.producer.username },
    href: seedPath(t.id),
    countsFor: t.id,
  };
}

/**
 * One song in the feed: who made it (and whose it grew from), its record, a
 * play button, its waveform, its numbers, and the ways to take it further.
 * Every take of the song is one tap away; the newest leads.
 */
export function FeedCardView({
  card,
  queue,
  trending,
  narrowWaveform,
}: {
  card: FeedCard;
  queue: PlayerTrack[];
  /** Its place in Discover's trending order, when in the top ten. */
  trending?: number;
  /** Shrink the waveform to give more room to the title (charts page). */
  narrowWaveform?: boolean;
}) {
  const [takeId, setTakeId] = useState(card.id);
  const take = card.takes.find((t) => t.id === takeId);
  const track = useMemo(() => feedTrack(card, take), [card, take]);
  const status = useTrackStatus(track.id);
  const props: CardProps = {
    card,
    track,
    queue,
    status,
    playable: track.url !== "",
    takeId,
    setTakeId,
    trending,
    narrowWaveform,
  };
  return <FeedTile {...props} />;
}

interface CardProps {
  card: FeedCard;
  track: PlayerTrack;
  narrowWaveform?: boolean;
  queue: PlayerTrack[];
  status: PlayerStatus | null;
  playable: boolean;
  takeId: string;
  setTakeId: (id: string) => void;
  trending?: number;
}

function FeedTile({ card, track, queue, status, playable, takeId, setTakeId, trending, narrowWaveform }: CardProps) {
  const newest = takeId === card.id;
  const cta = useFeedCta();
  const handle = card.producer.handle ?? card.producer.id;
  const player = usePlayer();
  const meta = [
    card.bpm ? `${Math.round(card.bpm)} BPM` : null,
    card.key,
    card.genre,
    card.stemCount > 0 ? `${card.stemCount} ${card.stemCount === 1 ? "track" : "tracks"}` : null,
  ].filter(Boolean);

  return (
    <article className={cn("group/card relative overflow-hidden rounded-2xl border bg-raised transition-colors", status ? "border-primary/60" : "border-hairline hover:border-strong")}>
      {/* Trending badge — top right */}
      <TrendingMark rank={trending} />
      {/* Top: cover (with play) + info + waveform + share */}
      <div className="flex items-center gap-4 p-5 pb-3">
        <SeedCover
          seed={{ id: card.id, title: card.title, genre: card.genre, bpm: card.bpm, key: card.key, producer: card.producer.username }}
          spinning={status === "playing"}
          interactive={playable}
          playStyle="overlay"
          playing={status === "playing"}
          remixBadge={card.remixOf ? "pill-sm" : undefined}
          onPlay={() => player.toggle(track, queue)}
          className="w-20 sm:w-24 shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <p className="flex items-center gap-2">
              <Link to={seedPath(track.id)} className="truncate text-lg font-bold leading-tight hover:underline">
                {card.title}
              </Link>
            </p>
            {/* Producer byline */}
            <p className="flex items-center gap-1.5 text-xs">
              <Link to={profilePath(handle)} className="shrink-0">
                <Avatar
                  name={card.producer.username}
                  url={card.producer.avatarUrl}
                  live={false}
                  className="size-5 text-[8px] !bg-white/10 !text-muted-foreground !ring-0"
                />
              </Link>
              <Link to={profilePath(handle)} className="font-medium hover:underline">
                {card.producer.username}
              </Link>
              <span className="text-muted-foreground">&middot; {relativeTime(card.createdAt)}</span>
              {card.remixOf && (
                <>
                  <span className="text-muted-foreground">&middot;</span>
                  <RemixByline origin={card.remixOf} className="min-w-0" />
                </>
              )}
            </p>
          </div>
          {/* Metadata pills */}
          {meta.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {meta.map((m) => (
                <span key={m} className="px-2 py-0.5 rounded-full border border-white/10 text-[10px] font-mono text-muted-foreground">
                  {m}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Waveform — inline right of track info */}
        <div className={cn("hidden sm:block min-w-0", narrowWaveform ? "w-[120px] shrink-0" : "flex-[2] max-w-[560px]")}>
          <Waveform
            track={playable ? track : null}
            waveform={newest ? card.waveform : null}
            queue={queue}
            className="h-16"
          />
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="size-9 shrink-0"
          onClick={() => void shareSeed(card)}
          title="Share"
          aria-label={`Share ${card.title}`}
        >
          <Share className="size-4" />
        </Button>
      </div>

      {/* Footer: stats (left) + remix CTAs (right) */}
      <footer className="flex items-center gap-x-0.5 border-t border-white/5 px-4 py-1.5">
        <LikeButton seedId={card.id} title={card.title} liked={card.likedByMe} count={card.likeCount} />
        <Stat icon={<Headphones className="size-3.5" />} value={card.playCount} label="plays" />
        <Stat
          icon={<MessageCircle className="size-3.5" />}
          value={card.commentCount}
          label="comments"
          to={`${seedPath(card.id)}#comments`}
        />
        <Stat
          icon={<GitBranch className="size-3.5" />}
          value={card.remixCount}
          label="remixes"
          to={`${seedPath(card.id)}#tree`}
        />
        {card.remixOf && <OriginalButton origin={card.remixOf} then={feedTrack(card)} />}
        <TakeSwitcher card={card} takeId={takeId} setTakeId={setTakeId} className="ml-1" />
        {/* Remix CTAs — right side; the jam picker's one action in their place */}
        {cta === "jam" ? (
          <StartJamButton seedId={takeId} size="chip" label="Start Jam" className="ml-auto" />
        ) : (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="hidden text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 sm:block">
              Remix in
            </span>
            <RemixCtas
              seedId={card.id}
              size="chip"
              seedTitle={card.title}
              producer={card.producer.username}
              trackCount={card.stemCount}
            />
          </div>
        )}
      </footer>
    </article>
  );
}

function TrendingMark({ rank }: { rank: number | undefined }) {
  if (!rank || rank > 10) return null;
  return (
    <span
      className="absolute top-3 right-3 rounded-full bg-[var(--changed)]/15 px-2 py-0.5 text-[10px] font-bold text-[var(--changed)] z-10"
      title={`#${rank} trending`}
    >
      #{rank}
    </span>
  );
}

/** The producer's takes of this song, oldest first; the chosen one plays. */
function TakeSwitcher({
  card,
  takeId,
  setTakeId,
  className,
}: {
  card: FeedCard;
  takeId: string;
  setTakeId: (id: string) => void;
  className?: string;
}) {
  if (card.takes.length < 2) return null;
  const takes = numberedTakes(card.takes);
  return (
    <select
      value={takeId}
      onChange={(e) => setTakeId(e.target.value)}
      aria-label={`Version of ${card.title}`}
      className={cn(
        "appearance-none cursor-pointer rounded-md border border-border bg-transparent px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors",
        className,
      )}
    >
      {takes.map((t) => (
        <option key={t.id} value={t.id} className="bg-card text-foreground">
          v{t.n}
        </option>
      ))}
    </select>
  );
}

function Stat({
  icon,
  value,
  label,
  to,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  to?: string;
}) {
  const body = (
    <>
      {icon}
      <span className="tabular">{value}</span>
    </>
  );
  const cls = "inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground";
  const a11y = { title: `${value} ${label}`, "aria-label": `${value} ${label}` };
  return to ? (
    <Link to={to} className={cn(cls, "hover:text-foreground")} {...a11y}>
      {body}
    </Link>
  ) : (
    <span className={cls} {...a11y}>
      {body}
    </span>
  );
}
